import { useId, type ReactNode } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  Archive,
  ChevronRight,
  Clock3,
  Database,
  ExternalLink,
  FileQuestion,
  FileText,
  FolderOpen,
  FolderOutput,
  Hand,
  History,
  LoaderCircle,
  Server,
  Zap,
} from 'lucide-react';
import type { ExportFiles } from '../shared/desktop-api';
import {
  exportSwitchStatus,
  exportWhenWords,
  type ExportStatusKind,
} from '../shared/export-presentation';
import { InfrastructureArt } from './InfrastructureArt';
export { exportStatus, exportTimeLabel } from '../shared/export-presentation';
import './export-route.css';

export type ExportStop =
  'when' | 'files' | 'destination' | 'backup' | 'elsewhere' | 'exceptions' | 'activity';
export type ExportTrigger = 'continuous' | 'scheduled' | 'manual';

export type { ExportStatusKind };

/**
 * "Export", its on/off switch (`power`, on every panel), and its status once: a dot and a few
 * words that open Activity.
 */
export function ExportHeader({
  status,
  active,
  onOpenActivity,
  power,
  children,
}: {
  status: { kind: ExportStatusKind; text: string };
  active: boolean;
  onOpenActivity: () => void;
  power?: ReactNode;
  children?: ReactNode;
}) {
  return (
    <div className="export-head">
      <h2>
        <FolderOutput size={22} aria-hidden="true" />
        Export
      </h2>
      {power}
      <div className="export-head-actions">
        {children}
        <button
          type="button"
          data-telemetry="export-stop-activity"
          className={`export-status is-${status.kind}${active ? ' active' : ''}`}
          aria-pressed={active}
          onClick={onOpenActivity}
          aria-label={`${status.text} · Activity`}
          title="Activity"
        >
          <i aria-hidden="true" />
          <span role="status">{status.text}</span>
          <ChevronRight size={15} aria-hidden="true" />
        </button>
      </div>
    </div>
  );
}

/**
 * Export's visible on/off switch (owner decision, 2026-10-26): "Export is on" or "Export is off"
 * with when documents leave in words beside it, its one action (Turn on or Turn off, drawn by the
 * card, which owns the save), and under it what that state means for this timing. It replaced the
 * hidden on/off state that only Start exporting could change.
 */
export function ExportPower({
  on,
  trigger,
  time,
  action,
  autopilot = false,
}: {
  on: boolean;
  trigger: ExportTrigger;
  /** The daily run's local time ("17:00"), for "daily at 5:00 PM". */
  time: string;
  action: ReactNode;
  /** The job is on Autopilot: its exceptions don't wait for the daily time (owner, 2026-10-31). */
  autopilot?: boolean;
}) {
  return (
    <div className={`export-power${on ? ' is-on' : ''}`} role="group" aria-label="Export on or off">
      <span className="export-power-state">
        <i aria-hidden="true" />
        <b>{on ? 'Export is on' : 'Export is off'}</b>
        <span className="export-power-when">· {exportWhenWords(trigger, timeLabel(time))}</span>
      </span>
      {action}
      <small className="export-power-status">
        {exportSwitchStatus(on, trigger, timeLabel(time), autopilot)}
      </small>
    </div>
  );
}

/**
 * When → Files → Destination, with Backup copy, Also send to and, on Autopilot, Exceptions
 * branching off the destination. The route never changes shape; choosing a stop changes only
 * the panel under it. While documents leave on their own, dots travel the
 * track, and they move only while the pointer or focus is in Export. Autopilot
 * is not a stop: it is set only on Review thresholds (owner decision, 2026-10-03).
 */
export function ExportRoute({
  stop,
  onStop,
  trigger,
  when,
  files,
  destination,
  destinationKind,
  backup,
  alsoSendTo,
  exceptions = false,
  flowing,
}: {
  stop: ExportStop;
  onStop: (stop: ExportStop) => void;
  trigger: ExportTrigger;
  when: string;
  files: string;
  destination: string;
  destinationKind: 'filesystem' | 'content-central';
  backup: string;
  /** Whether a cloud copy can be set up here; without one the stop is not drawn. */
  alsoSendTo: boolean;
  /** The job is on Autopilot, the only mode that uses the exceptions folder (ADR-0096); on Manual the stop is not drawn. */
  exceptions?: boolean;
  flowing: boolean;
}) {
  const node = (id: ExportStop, label: string, icon: ReactNode, value: string) => (
    <button
      type="button"
      data-stop={id}
      data-telemetry={`export-stop-${id}`}
      className={`export-route-stop${stop === id ? ' selected' : ''}`}
      aria-pressed={stop === id}
      aria-label={`${label}: ${value}`}
      onClick={() => onStop(id)}
    >
      <span className="export-route-icon" aria-hidden="true">
        {icon}
      </span>
      <span className="export-route-text">
        <small>{label}</small>
        <b>{value}</b>
      </span>
    </button>
  );
  const branch = (id: ExportStop, icon: ReactNode, label: string, value?: string) => (
    <button
      type="button"
      data-stop={id}
      data-telemetry={`export-stop-${id}`}
      className={`export-route-branch-stop${stop === id ? ' selected' : ''}`}
      aria-pressed={stop === id}
      onClick={() => onStop(id)}
    >
      {icon}
      <span>{label}</span>
      {value && <b>{value}</b>}
    </button>
  );
  return (
    <div className="export-route" role="group" aria-label="Export route">
      <div className="export-route-line">
        <div className="export-route-track" aria-hidden="true">
          {flowing && (
            <>
              <i className="export-route-flow" />
              <i className="export-route-flow" style={{ animationDelay: '-2.5s' }} />
              <i className="export-route-flow" style={{ animationDelay: '-5s' }} />
            </>
          )}
        </div>
        {node(
          'when',
          'When',
          trigger === 'continuous' ? (
            <Zap size={19} />
          ) : trigger === 'scheduled' ? (
            <Clock3 size={19} />
          ) : (
            <Hand size={19} />
          ),
          when,
        )}
        <ChevronRight className="export-route-arrow" size={15} aria-hidden="true" />
        {node('files', 'Files', <FileText size={19} />, files)}
        <ChevronRight className="export-route-arrow" size={15} aria-hidden="true" />
        {node(
          'destination',
          'Destination',
          destinationKind === 'filesystem' ? <FolderOpen size={19} /> : <Database size={19} />,
          destination,
        )}
      </div>
      <div className="export-route-branch">
        <span className="export-route-branch-line" aria-hidden="true" />
        <div className="export-route-branches">
          {branch('backup', <Archive size={16} aria-hidden="true" />, 'Backup copy', backup)}
          {alsoSendTo &&
            branch('elsewhere', <Server size={16} aria-hidden="true" />, 'Also send to')}
          {exceptions &&
            branch('exceptions', <FileQuestion size={16} aria-hidden="true" />, 'Exceptions')}
        </div>
      </div>
    </div>
  );
}

/** The panel under the route. */
export function ExportStage({
  icon,
  title,
  className,
  children,
  aside,
}: {
  icon: ReactNode;
  title: string;
  className?: string;
  children: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <section className={`export-stage${className ? ` ${className}` : ''}`} aria-label={title}>
      <header className="export-stage-head">
        {icon}
        <h3>{title}</h3>
        {aside}
      </header>
      {children}
    </section>
  );
}

const hours = [
  { at: 0, label: '12 AM' },
  { at: 25, label: '6 AM' },
  { at: 50, label: 'Noon' },
  { at: 75, label: '6 PM' },
  { at: 100, label: '12 AM' },
];
const workTicks = [
  35, 36.8, 38.6, 41, 42.4, 44.9, 47.2, 49.6, 51.2, 54, 56.4, 58.1, 61, 63.5, 65.2, 68.3,
];

function timeLabel(value: string) {
  const [hour, minute] = value.split(':').map(Number);
  if (!Number.isFinite(hour) || !Number.isFinite(minute)) return value;
  const date = new Date(2000, 0, 1, hour, minute);
  return date.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}

export function triggerSummary(trigger: ExportTrigger, time: string) {
  return trigger === 'continuous'
    ? 'As soon as ready'
    : trigger === 'scheduled'
      ? `Daily at ${timeLabel(time)}`
      : 'Only when I say';
}

/** When documents leave: three choices over a 24-hour strip that draws what each means. */
export function ExportWhenPanel({
  trigger,
  time,
  locked,
  notice,
  onTrigger,
  onTime,
}: {
  trigger: ExportTrigger;
  time: string;
  locked: boolean;
  notice?: ReactNode;
  onTrigger: (trigger: ExportTrigger) => void;
  onTime: (time: string) => void;
}) {
  const [hour, minute] = time.split(':').map(Number);
  const pin =
    Number.isFinite(hour) && Number.isFinite(minute) ? ((hour * 60 + minute) / 1440) * 100 : 75;
  const chip = (value: ExportTrigger, icon: ReactNode, label: string) => (
    <button
      type="button"
      disabled={locked}
      aria-pressed={trigger === value}
      className={`export-chip${trigger === value ? ' selected' : ''}`}
      onClick={() => onTrigger(value)}
    >
      {icon}
      {label}
    </button>
  );
  return (
    <ExportStage icon={<Clock3 size={18} aria-hidden="true" />} title="When documents leave">
      {notice}
      <div className="export-chips" role="group" aria-label="When documents leave">
        {chip('continuous', <Zap size={16} aria-hidden="true" />, 'As soon as they’re ready')}
        {chip('scheduled', <Clock3 size={16} aria-hidden="true" />, 'Once a day')}
        {chip('manual', <Hand size={16} aria-hidden="true" />, 'Only when I say')}
        {trigger === 'scheduled' && (
          <label className="export-time">
            <span>at</span>
            <input
              type="time"
              disabled={locked}
              value={time}
              onChange={(event) => onTime(event.target.value)}
              aria-label="Local time"
            />
          </label>
        )}
      </div>
      <div className="export-day" aria-hidden="true">
        <div className="export-day-strip">
          <i className="export-day-work" />
          {trigger === 'continuous' &&
            workTicks.map((tick) => (
              <i key={tick} className="export-day-tick" style={{ left: `${tick}%` }} />
            ))}
          {trigger === 'scheduled' && (
            <>
              <i className="export-day-pin" style={{ left: `${pin}%` }} />
              <span className="export-day-pin-label" style={{ left: `${pin}%` }}>
                {timeLabel(time)}
              </span>
            </>
          )}
          {trigger === 'manual' && (
            <span className="export-day-hand">
              <Hand size={16} />
              Nothing leaves until you click Export now
            </span>
          )}
        </div>
        <div className="export-day-hours">
          {hours.map((item) => (
            <span key={item.at} style={{ left: `${item.at}%` }}>
              {item.label}
            </span>
          ))}
        </div>
      </div>
      <p className="export-stage-caption">
        {trigger === 'continuous'
          ? 'Each document leaves on its own the moment it’s ready, all day.'
          : trigger === 'scheduled'
            ? `Ready documents wait here and leave together at ${timeLabel(time)}, this computer’s time.`
            : 'Ready documents wait here until someone clicks Export now.'}
      </p>
    </ExportStage>
  );
}

/**
 * A small picture of one exported file: the searchable PDF (scanned page, found
 * text marked), the data file (a table of values) or the text file (words only).
 * Authored line art in the illustration style: currentColor outlines, raised
 * faces, one orange mark. Decorative; the tile names the file.
 */
export function ExportFileArt({ kind }: { kind: 'pdf' | 'data' | 'text' }) {
  const hatch = `xa-hatch-${useId().replace(/:/g, '')}`;
  return (
    <svg className={`export-file-art is-${kind}`} viewBox="0 0 120 100" aria-hidden="true">
      <defs>
        <pattern id={hatch} width="5" height="5" patternUnits="userSpaceOnUse">
          <path d="M0 5L5 0" stroke="currentColor" strokeWidth=".6" opacity=".35" />
        </pattern>
      </defs>
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path
          className="xa-sheet"
          d="M31 7h42l17 17v67a4 4 0 0 1-4 4H31a4 4 0 0 1-4-4V11a4 4 0 0 1 4-4Z"
          fill="var(--cp-surface-raised)"
        />
        <path d="M73 7v13a4 4 0 0 0 4 4h13" />
        {kind === 'pdf' && (
          <>
            <rect
              x="36"
              y="29"
              width="45"
              height="21"
              rx="1.5"
              fill={`url(#${hatch})`}
              strokeWidth="1"
              opacity=".75"
            />
            <rect
              x="34"
              y="61"
              width="41"
              height="8"
              rx="2"
              fill="var(--cp-orange)"
              stroke="none"
              opacity=".38"
            />
            <path d="M37 57h43M37 65h35M37 73h41M37 81h26" strokeWidth="2" opacity=".45" />
            <circle cx="87" cy="75" r="9" fill="var(--cp-surface-raised)" />
            <path d="M93.5 81.5l8 8" strokeWidth="3" />
          </>
        )}
        {kind === 'data' && (
          <>
            <rect
              x="35"
              y="28"
              width="47"
              height="9"
              fill="var(--cp-orange)"
              stroke="none"
              opacity=".4"
            />
            <rect x="35" y="28" width="47" height="45" rx="1.5" strokeWidth="1" />
            <path d="M35 37h47M35 46h47M35 55h47M35 64h47M53 28v45" strokeWidth="1" opacity=".55" />
            <path d="M39 41.5h9M39 50.5h10M39 59.5h8M39 68.5h10" strokeWidth="2" opacity=".3" />
            <path d="M57 41.5h17M57 50.5h12M57 59.5h20M57 68.5h14" strokeWidth="2" opacity=".6" />
            <path d="M35 83h28" strokeWidth="2" opacity=".3" />
          </>
        )}
        {kind === 'text' && (
          <>
            <path
              d="M36 29h45M36 36h38M36 43h43M36 50h31M36 57h45M36 64h40M36 71h44M36 78h24"
              strokeWidth="2"
              opacity=".45"
            />
            <path d="M64 74v9" stroke="var(--cp-orange)" strokeWidth="2.5" />
          </>
        )}
      </g>
    </svg>
  );
}

/** The Files stop's value on the route: what each document arrives as. */
export const filesSummary = (files: ExportFiles, archive: boolean) =>
  `${archive ? 'PDF/A' : 'PDF'}${files === 'pdf-only' ? ' only' : ' + data + text'}`;

/**
 * What each document arrives as, each file a picture with what it is for. A folder export
 * chooses between the PDF with its data and text files (the default) and the PDF alone
 * (ADR-0096), as two chips like When's; the tiles under them are the facts of the chosen one,
 * so nothing on them looks ticked or selected. The other choice, archive-grade PDF, is a
 * switch beneath them. Without `onFiles` (Content Central) there is no choice to make.
 */
export function ExportFilesPanel({
  archive,
  available,
  locked,
  onArchive,
  files = 'pdf-data-text',
  onFiles,
}: {
  archive: boolean;
  available: boolean;
  locked: boolean;
  onArchive: (archive: boolean) => void;
  files?: ExportFiles;
  onFiles?: (files: ExportFiles) => void;
}) {
  const tile = (kind: 'pdf' | 'data' | 'text', name: string, use: string) => (
    <div className="export-tile export-file-tile" role="listitem">
      <span className="export-file-art-wrap">
        <ExportFileArt kind={kind} />
      </span>
      <b>{name}</b>
      <small>{use}</small>
    </div>
  );
  const pdfOnly = Boolean(onFiles) && files === 'pdf-only';
  const chip = (value: ExportFiles, label: string) => (
    <button
      type="button"
      disabled={locked}
      aria-pressed={files === value}
      className={`export-chip${files === value ? ' selected' : ''}`}
      onClick={() => onFiles?.(value)}
    >
      {label}
    </button>
  );
  return (
    <ExportStage
      icon={<FileText size={18} aria-hidden="true" />}
      title="Each document arrives as"
      className="export-files-stage"
    >
      {onFiles && (
        <div className="export-chips" role="group" aria-label="Files for each document">
          {chip('pdf-data-text', 'PDF + data + text files')}
          {chip('pdf-only', 'PDF only')}
        </div>
      )}
      <div
        className={`export-tiles${pdfOnly ? ' is-single' : ''}`}
        role="list"
        aria-label="Files made for each document"
      >
        {tile(
          'pdf',
          archive ? 'PDF/A' : 'Searchable PDF',
          pdfOnly
            ? 'The scanned pages, with text you can find and copy, and every value and note inside'
            : 'The scanned pages, with text you can find and copy',
        )}
        {!pdfOnly && tile('data', 'Data file', 'Every captured value, ready for other software')}
        {!pdfOnly && tile('text', 'Text file', 'All the words from the pages')}
      </div>
      <label className="export-well export-switch-row">
        <span>
          <b>Archive-grade PDF</b>
          <small>
            {available
              ? 'PDF/A, for records you keep for years. Checked before it goes.'
              : 'Available once the optional archival checker is installed on this computer.'}
          </small>
        </span>
        <input
          type="checkbox"
          role="switch"
          className="export-switch"
          checked={archive}
          disabled={locked || (!available && !archive)}
          onChange={(event) => onArchive(event.target.checked)}
          aria-label="Archive-grade PDF"
        />
      </label>
    </ExportStage>
  );
}

const retentionChoices: Array<{ value: number | null; label: string }> = [
  { value: 30, label: '30 days' },
  { value: 60, label: '60 days' },
  { value: 90, label: '90 days' },
  { value: 180, label: '180 days' },
  { value: 365, label: '1 year' },
  { value: null, label: 'Forever' },
];
export const retentionSummary = (days: number | null) =>
  retentionChoices.find((item) => item.value === days)?.label ?? `${days} days`;

/** Paige's own copy of everything it exports. */
export function ExportBackupPanel({
  retentionDays,
  location,
  space,
  locked,
  onRetention,
}: {
  retentionDays: number | null;
  location: string;
  /** The folder's disk, when it is the disk the storage folder is on (the one disk the app can measure). */
  space?: { free: string; total: string } | null;
  locked: boolean;
  onRetention: (days: number | null) => void;
}) {
  // Laid out like Settings › Storage (owner, 2026-10-04): the folder in its own box, the why
  // said once, how long as one row of chips, and the storage drawing beside it. The folder is Paige's own
  // data (owner, 2026-10-30), always in the data folder, so it has no Change folder: a job that saved a chosen one
  // before keeps it, and the line shows it.
  return (
    <ExportStage
      icon={<Archive size={18} aria-hidden="true" />}
      title="Backup copy"
      className="export-backup-stage"
    >
      <div className="export-backup-card">
        <InfrastructureArt kind="storage" />
        <p className="export-backup-path">{location}</p>
        {space && (
          <p className="export-backup-space">
            <strong>{space.free} free</strong> of {space.total} on this disk
          </p>
        )}
        <p className="export-backup-why">
          Paige keeps its own copy of everything it exports, in case a file is lost. Cleanup removes
          only these copies.
        </p>
        <div className="export-backup-keep">
          <span>Keep copies for</span>
          <div className="export-chips" role="group" aria-label="Keep backup copies for">
            {retentionChoices.map((choice) => (
              <button
                key={choice.label}
                type="button"
                disabled={locked}
                aria-pressed={retentionDays === choice.value}
                className={`export-chip${retentionDays === choice.value ? ' selected' : ''}`}
                onClick={() => onRetention(choice.value)}
              >
                {choice.label}
              </button>
            ))}
          </div>
        </div>
      </div>
    </ExportStage>
  );
}

/**
 * Autopilot's exceptions folder (ADR-0096): where a document goes when it can't be filed as one
 * of the job's types, so nothing stays behind. Laid out as Backup copy is: the folder in its own
 * box, the why said once, and Change folder. It is always a local folder and is never cleared: the
 * main process fills in `<Documents>\Paige Jobs\<job>\exceptions` (`%ProgramData%\Ademero\Paige Jobs\<job>\exceptions`
 * under the service) and the engine saves it. When that
 * default could not be made (`location` null: the engine holds these documents until there is a folder),
 * the box says no folder is chosen yet and Choose a folder asks for one. Each document arrives there as one
 * PDF, whatever Files says. On a job exporting to
 * Content Central (`contentCentral`) a document whose type isn't matched there, whose Content Central type changed,
 * or with a value Content Central refuses goes there too (decisions 19 and 23 of docs/planning/exceptions-destination.md).
 * `onOpen`, once the folder is saved: Open folder, as the watched folder's. `held`: the job's settings are saved
 * without a folder, so these documents wait in Paige until one is chosen, and the panel says so; a job with
 * nothing saved yet gets its default at its first Save, so nothing is said of waiting.
 */
export function ExportExceptionsPanel({
  location,
  held = false,
  contentCentral = false,
  choosing,
  locked,
  onChangeLocation,
  onOpen,
}: {
  location: string | null;
  held?: boolean;
  contentCentral?: boolean;
  choosing: boolean;
  locked: boolean;
  onChangeLocation: () => void;
  onOpen?: () => void;
}) {
  return (
    <ExportStage
      icon={<FileQuestion size={18} aria-hidden="true" />}
      title="Documents Paige can’t file"
      className="export-backup-stage export-exceptions-stage"
    >
      <div className="export-backup-card">
        <InfrastructureArt kind="storage" />
        <p className={`export-backup-path${location ? '' : ' is-missing'}`}>
          {location ?? 'No folder chosen yet'}
        </p>
        <p className="export-backup-why">
          {contentCentral
            ? 'On Autopilot, documents that aren’t one of this job’s document types, can’t be read, or have a type or value Content Central can’t take, go here so nothing stays behind in Paige.'
            : 'On Autopilot, documents that aren’t one of this job’s document types, or can’t be read, go here so nothing stays behind in Paige.'}{' '}
          Each arrives as a PDF with the reason in its name.
        </p>
        {!location && held && (
          <p className="export-save-note" role="status">
            <FolderOpen size={15} aria-hidden="true" /> Choose a folder for them. Until then they
            wait in Paige.
          </p>
        )}
        <div className="export-backup-actions">
          {location && onOpen && (
            <button
              type="button"
              className="secondary-button export-exceptions-open"
              data-telemetry="exceptions-open"
              onClick={onOpen}
            >
              <ExternalLink size={16} aria-hidden="true" /> Open folder
            </button>
          )}
          <button
            type="button"
            className="secondary-button export-backup-change"
            disabled={locked || choosing}
            onClick={onChangeLocation}
          >
            {choosing ? (
              <>
                <LoaderCircle className="spin" size={15} aria-hidden="true" /> Choosing…
              </>
            ) : (
              <>
                <FolderOpen size={16} aria-hidden="true" />{' '}
                {location ? 'Change folder' : 'Choose a folder'}
              </>
            )}
          </button>
        </div>
      </div>
    </ExportStage>
  );
}

export { History as ActivityIcon };
